/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { gql, useQuery } from '@apollo/client';

/**
 * @url [used fields](https://friendliai.canny.io/admin/settings/fields/post-fields)
 * @url [docs](https://help.canny.io/en/articles/6329585-customizing-fields)
 */
type CannyPostFieldCategories = 'supported-model' | 'feature-requests-feedback';

export function useOpenCanny({
  postFieldCategory = 'feature-requests-feedback',
}: {
  postFieldCategory?: CannyPostFieldCategories;
}) {
  const UserInformationDocument = gql`
    query UserInformation {
      clientUser {
        avatarUrl
        createdAt
        email
        id
        name
        team {
          id
        }
      }
    }
  `;

  const { data } = useQuery(UserInformationDocument);

  const userInformation = data?.clientUser;

  /**
   * @url https://developers.canny.io/install
   * @note CANNY_APP_ID is not secret
   */
  const CANNY_APP_ID = '663ab79bb0395be041984eb5';

  const handleFeedback = () => {
    window.Canny(
      'identify',
      {
        appID: CANNY_APP_ID,
        user: {
          email: userInformation?.email,
          name: userInformation?.name,
          id: userInformation?.id,
          avatarURL: userInformation?.avatarUrl,
          created: new Date(userInformation?.createdAt).toISOString(),
          customFields: {
            teamId: userInformation?.team?.id,
          },
        },
      },
      /**
       * @minpeter TODO
       * (p5) No need to identify on every feedback, extract from callback
       */
      () => {
        const authenticatedLink = window.Canny(
          'authenticateCannyLink',
          'friendliai.canny.io/' + postFieldCategory
        );

        window.open('https://' + authenticatedLink, '_blank');
      }
    );
  };

  return [handleFeedback] as const;
}
